import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import PageHeader from '../ui/PageHeader';

const coreValuesData = [
  {
    title: 'Water Independence',
    description:
      'We want every swimmer to feel genuinely at ease in the water - not just safe under supervision. That means building the right foundations: breathing, body position, and real comfort in the water. Safety isn\'t a box we tick; it\'s woven into every lesson from day one.',
  },
  {
    title: 'Technique First',
    description:
      'We don\'t measure progress by how quickly a swimmer levels up. We measure it by whether the technique actually sticks. Our coaches take the time to build strong foundations before moving forward - because when a swimmer truly understands what they\'re doing, the results follow naturally.',
  },
  {
    title: 'Confidence at Every Level',
    description:
      'We work with swimmers from age 4 right through to high school, and we take pride in meeting every child where they are. Every milestone matters - from a first nervous dive to a powerful stroke. Our goal isn\'t just better swimming; it\'s swimmers who genuinely love being in the water.',
  },
  {
    title: 'More Than a Swim School',
    description:
      'We\'ve been teaching many of our families for years - watching the same kids grow from cautious beginners into strong swimmers. Some have even come back to join the coaching team. Those long-term connections aren\'t an accident; they\'re what happens when coaches genuinely invest in the people they teach.',
  },
  {
    title: 'A Skill for Life',
    description:
      'Swimming is something people carry with them forever. We want every swimmer who comes through SJ to leave with more than a certificate - a real love for the water and the confidence to keep going long after lessons are done.',
  },
];

const CoreValues = () => {
  const theme = useTheme();
  const [activeValue, setActiveValue] = useState(null);

  return (
    <Container sx={{ mt: 4, mb: 5 }}>
      <PageHeader title="Our Core Values" inView mb={4} />

      {/* Values + Images */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
      <Grid container spacing={4} alignItems="stretch">
        {/* Left Side: Images that fill the accordion height */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', order: { xs: 2, md: 1 } }}>
          <Box sx={{
            flex: 1,
            display: 'grid',
            gridTemplateRows: '1fr 1fr',
            gap: 2,
            minHeight: { xs: 560, md: 0 },
          }}>
            <Box
              component="img"
              src="/images/SJ_Gallery/LTS_1.jpg"
              alt="Core Values 1"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', borderRadius: 3, display: 'block' }}
            />
            <Box
              component="img"
              src="/images/SJ_Gallery/SQ_Su_4.jpg"
              alt="Core Values 2"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', borderRadius: 3, display: 'block' }}
            />
          </Box>
        </Grid>

        {/* Right Side: Core Values Accordions */}
        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          {coreValuesData.map((value, index) => (
            <Box
              key={index}
              component={motion.div}
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: index * 0.1 }}
              role="button"
              tabIndex={0}
              aria-expanded={activeValue === index}
              aria-label={`${value.title} - read more`}
              onClick={() => setActiveValue(activeValue === index ? null : index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveValue(activeValue === index ? null : index);
                }
              }}
              sx={{
                borderRadius: '20px',
                background: theme.palette.background.paper,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: theme.palette.text.primary,
                boxShadow: '0px 6px 24px rgba(0,0,0,0.3)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                cursor: 'pointer',
                mb: 3,
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 30px rgba(0,0,0,0.7)',
                },
              }}
            >
              {/* Header - always visible */}
              <Box sx={{ px: 3, pt: 3, pb: activeValue === index ? 1 : 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {value.title}
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                    transform: activeValue === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    fontSize: 20,
                    opacity: 0.55,
                    flexShrink: 0,
                    color: theme.palette.text.secondary,
                  }}
                />
              </Box>

              {/* Description - accordion expand via grid-template-rows */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: activeValue === index ? '1fr' : '0fr',
                  transition: 'grid-template-rows 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{ px: 3, pb: 3, lineHeight: 1.7, color: theme.palette.text.secondary, fontWeight: 400 }}
                  >
                    {value.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>
      </Box>
    </Container>
  );
};

export default CoreValues;